import { createContext, useContext, useState, useEffect } from "react";
import constantObj from "../constant/constant";

const SocialContext = createContext();

const CACHE_KEY = "social_links_cache";
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_DURATION;

    if (isExpired) {
      localStorage.removeItem(CACHE_KEY); // clean up expired cache
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

const setCacheData = (data) => {
  try {
    const payload = { data, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // silently fail if localStorage is unavailable
  }
};

export const SocialProvider = ({ children }) => {
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSocialMedia = async () => {
      // 1. Check cache first
      const cached = getCachedData();
      if (cached) {
        setSocials(cached);
        setLoading(false);
        return; // ✅ Skip fetch entirely
      }

      // 2. Cache miss or expired — fetch fresh data
      try {
        setLoading(true);
        const response = await fetch(
          `${constantObj?.API_URL_DEV}/admin/social`
        );

        if (!response.ok) throw new Error("Failed to fetch social media links");

        const data = await response.json();
        const result = data.data || [];

        setSocials(result);
        setCacheData(result); // 💾 Save to cache with timestamp
        setError(null);
      } catch (err) {
        setError(err.message);
        setSocials([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSocialMedia();
  }, []);

  return (
    <SocialContext.Provider value={{ socials, loading, error }}>
      {children}
    </SocialContext.Provider>
  );
};

export const useSocial = () => {
  const context = useContext(SocialContext);
  if (context === undefined) {
    throw new Error("useSocial must be used within SocialProvider");
  }
  return context;
};